import { useState } from "react";
import {
    FormProvider,
    useForm,
    Controller,
    type ControllerRenderProps,
    type Resolver,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { X } from "lucide-react";
import { TailwindModal } from "../../../components/TailwindModal";
import { FTPInput } from "../../../components-new";
import { createNotebookSchema, type CreateNotebookFormData } from "./create-notebook-schema";
import { useCreateNotebook } from "../tars-hooks/useCreateNotebook";
import { NOTEBOOK_COLORS, NOTEBOOK_ICONS, ColorPicker, IconPicker } from "./utils";

const MAX_TAGS = 10;
const TAG_INPUT_PLACEHOLDER = "Type and press Enter";
const TAG_LABEL = "Add tags";

interface TagsFieldProps {
    field: ControllerRenderProps<CreateNotebookFormData, "tags">;
    selectedColor: string;
}

const TagsField = ({ field, selectedColor }: TagsFieldProps) => {
    const [inputValue, setInputValue] = useState("");

    const handleAddTag = () => {
        const trimmed = inputValue.trim();
        if (trimmed && field.value.length < MAX_TAGS && !field.value.includes(trimmed)) {
            field.onChange([...field.value, trimmed]);
        }
        setInputValue("");
    };

    const handleRemoveTag = (tagToRemove: string) => {
        field.onChange(field.value.filter((tag) => tag !== tagToRemove));
    };

    const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            handleAddTag();
        }
        if (e.key === "Backspace" && !inputValue && field.value.length > 0) {
            handleRemoveTag(field.value[field.value.length - 1]);
        }
    };

    return (
        <div>
            <label className="mb-2 block text-[14px] font-medium text-[#1b1b1d]">{TAG_LABEL}</label>
            <div className="flex min-h-[56px] flex-wrap items-center gap-2 rounded-xl border px-3 py-2">
                {field.value.map((tag: string, index: number) => (
                    <div
                        key={index}
                        className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium"
                        style={{
                            backgroundColor: selectedColor,
                            color: "#1b1b1d",
                        }}
                    >
                        {tag}
                        <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex h-4 w-4 items-center justify-center rounded-full bg-black/20 text-xs font-bold transition hover:bg-black/40"
                        >
                            ×
                        </button>
                    </div>
                ))}
                {field.value.length < MAX_TAGS && (
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleTagInputChange}
                        onKeyDown={handleTagInputKeyDown}
                        className="min-w-[120px] flex-1 text-sm outline-none"
                        placeholder={TAG_INPUT_PLACEHOLDER}
                    />
                )}
            </div>
        </div>
    );
};

interface CreateNotebookModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CreateNotebookModal = ({ isOpen, onClose }: CreateNotebookModalProps) => {
    const { mutate: createNotebook, isLoading } = useCreateNotebook();

    const methods = useForm<CreateNotebookFormData>({
        resolver: yupResolver(createNotebookSchema) as Resolver<CreateNotebookFormData>,
        defaultValues: {
            name: "",
            tags: [],
            color: NOTEBOOK_COLORS[0],
            icon: NOTEBOOK_ICONS[0],
        },
        mode: "onBlur",
    });

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = methods;

    const handleSubmitForm = (data: CreateNotebookFormData) => {
        createNotebook(
            {
                name: data.name,
                tags: data.tags,
                more: {
                    color: data.color,
                    icon: data.icon,
                },
            },
            {
                onSuccess: () => {
                    handleClose();
                },
            }
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <TailwindModal isOpen={isOpen} onClose={handleClose}>
            <FormProvider {...methods}>
                <div className="mx-auto w-full max-w-2xl rounded-xl bg-white p-6">
                    <div className="mb-6 flex items-center justify-between">
                        <h2 className="text-[20px] font-bold text-[#1b1b1d]">Create a Notebook</h2>
                        <button
                            onClick={handleClose}
                            className="rounded-md p-1 transition-colors hover:bg-gray-100"
                            disabled={isLoading}
                        >
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit(handleSubmitForm)}>
                        <div className="space-y-6">
                            <FTPInput name="name" label="Notebook Name" placeholder="Name your Notebook" />

                            {/* <FTPInput
                                name="tags"
                                label="Add tags"
                                placeholder="Tag your Notebook..."
                            /> */}

                            <Controller
                                name="tags"
                                control={control}
                                render={({ field }) => (
                                    <TagsField field={field} selectedColor={methods.watch("color")} />
                                )}
                            />

                            <div>
                                <label className="mb-3 block text-[14px] font-medium text-[#1b1b1d]">
                                    Notebook color
                                </label>
                                <Controller
                                    name="color"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <ColorPicker value={field.value} onChange={field.onChange} />
                                            {errors.color && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.color.message}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>

                            <div>
                                <label className="mb-3 block text-[14px] font-medium text-[#1b1b1d]">
                                    Notebook icon
                                </label>
                                <Controller
                                    name="icon"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <IconPicker value={field.value} onChange={field.onChange} />
                                            {errors.icon && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.icon.message}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isLoading}
                                    variant="contained"
                                    className="rounded-lg px-6 py-2 text-[14px] font-bold normal-case disabled:cursor-not-allowed disabled:opacity-50"
                                    sx={{
                                        textTransform: "none",
                                        boxShadow: "none",
                                        "&&": {
                                            backgroundColor: "#E5E7EB",
                                            color: "#1b1b1d",
                                        },
                                        "&:hover": {
                                            backgroundColor: "#D1D5DB",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    variant="contained"
                                    className="rounded-lg px-6 py-2 text-[14px] font-bold normal-case disabled:cursor-not-allowed disabled:opacity-50"
                                    sx={{
                                        textTransform: "none",
                                        boxShadow: "none",
                                        "&&": {
                                            backgroundColor: "#1b1b1d",
                                            color: "white",
                                        },
                                        "&:hover": {
                                            backgroundColor: "#A5F455",
                                            color: "#1b1b1d",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    {isLoading ? "Creating..." : "Create Notebook"}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </FormProvider>
        </TailwindModal>
    );
};
